import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, Subject, forkJoin, map, of, tap } from 'rxjs';
import { environment } from '../../environments/environment';

/** A short-lived read link to an agent's profile picture (null when none is set). */
export interface AgentProfilePicture {
  agentId: string;
  url: string | null;
  updatedAt: string | null;
}

/** A saved change to one agent's picture; picture is null after a removal. */
export interface AgentProfilePictureChange {
  agentId: string;
  picture: AgentProfilePicture | null;
}

/** Limits mirrored from the API so bad files are caught before upload. */
export const PROFILE_PICTURE_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
export const PROFILE_PICTURE_MAX_BYTES = 5 * 1024 * 1024;

/** The API accepts at most this many ids per lookup (keeps the query string short). */
const MAX_IDS_PER_LOOKUP = 150;

@Injectable({ providedIn: 'root' })
export class AgentProfilePictureService {
  private readonly baseUrl = `${environment.apiConfig.uri}/api/Agent`;
  private readonly changesSubject = new Subject<AgentProfilePictureChange>();

  /**
   * Every saved upload or removal. Emitted by the service, so pages still see a change
   * that finishes after the editor that started it has been closed.
   */
  readonly changes$ = this.changesSubject.asObservable();

  constructor(private http: HttpClient) {}

  upload(agentId: string, file: File): Observable<AgentProfilePicture> {
    const form = new FormData();
    form.append('picture', file, file.name);
    return this.http.put<AgentProfilePicture>(
      `${this.baseUrl}/${encodeURIComponent(agentId)}/profile-picture`,
      form
    ).pipe(tap(picture => this.changesSubject.next({ agentId, picture })));
  }

  remove(agentId: string): Observable<void> {
    return this.http
      .delete<void>(`${this.baseUrl}/${encodeURIComponent(agentId)}/profile-picture`)
      .pipe(tap(() => this.changesSubject.next({ agentId, picture: null })));
  }

  /** Links for the given agents; agents without a picture are left out. */
  getMany(agentIds: string[]): Observable<AgentProfilePicture[]> {
    if (agentIds.length === 0) {
      return of([]);
    }
    const batches: string[][] = [];
    for (let i = 0; i < agentIds.length; i += MAX_IDS_PER_LOOKUP) {
      batches.push(agentIds.slice(i, i + MAX_IDS_PER_LOOKUP));
    }
    return forkJoin(
      batches.map(ids => {
        const query = new URLSearchParams({ agentIds: ids.join(',') }).toString();
        return this.http.get<AgentProfilePicture[]>(`${this.baseUrl}/profile-pictures?${query}`);
      })
    ).pipe(map(results => results.flat()));
  }
}
